/**
 * Utility functions for handling names and fallback display logic
 */

interface NameData {
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * Get a display name with fallback to combined first and last name
 * @param value - The primary field value (e.g., displayName, fullName)
 * @param nameData - Object containing firstName and lastName
 * @param fallback - Default fallback text if everything is empty (default: 'Unknown')
 * @returns Display name string
 */
export function getDisplayName(
  value: string | null | undefined,
  nameData: NameData,
  fallback: string = "Unknown"
): string {
  // If the primary value exists and is not empty, use it
  if (value?.trim()) {
    return value.trim();
  }

  // Otherwise, try to combine firstName and lastName
  const { firstName, lastName } = nameData;
  const combinedName = [firstName, lastName]
    .filter((name) => name?.trim())
    .join(" ")
    .trim();

  // Return combined name if it exists, otherwise use fallback
  return combinedName || fallback;
}

/**
 * Get a combined full name from firstName and lastName
 * @param nameData - Object containing firstName and lastName
 * @param fallback - Default fallback text if both names are empty (default: 'Unknown')
 * @returns Combined full name string
 */
export function getFullName(nameData: NameData, fallback: string = "Unknown"): string {
  const { firstName, lastName } = nameData;
  const combinedName = [firstName, lastName]
    .filter((name) => name?.trim())
    .join(" ")
    .trim();

  return combinedName || fallback;
}
